/**
 * Krok UX1 — TYP PŘEDMĚTU ZAKÁZKY, JEDEN ZDROJ PRAVDY.
 *
 * Nález AUD1 28. 9. 2026 (obrazovka zakázky Petrovice 475): u zakázky
 * s 8 parcelami visel štítek „Dům". Příčina je dvojí:
 *
 *  1. `dd_cases.kind` je to, co makléř vybral v průvodci při založení
 *     (`StartAuditModal.tsx` → `POST /v1/duediligence/cases`). Nikdo ho
 *     nepřepočítává po tom, co audit našel.
 *  2. UI navíc zatlacilo enum na binární ternár
 *     `header.kind === 'HOUSE' ? 'HOUSE' : 'LAND'`, takže `FLAT` (byt)
 *     se vykreslil jako „Pozemek" a stavba se stejným štítkem tvářila,
 *     jako by na ní nic nebylo.
 *
 * ROZHODNUTÍ (krok UX1): počítá se to v DISPLAY vrstvě web-adminu, ne
 * v `packages/contexts/duediligence` — ten je zlatý standard a výpočet
 * se v tomto kroku nesmí měnit (`step-UX1` bod 3). Z toho plyne i
 * upřímnost: když zadání makléře odporuje tomu, co je v datech, UI to
 * přizná vedle skutečného popisku, místo aby mlčelo a klamalo.
 *
 * Data (parcele) bereme ze stejného resolveru jako hlavička a plocha
 * (`caseHeader` v `@reos/reports`), stavbu z faktů `building.*`, uzlu
 * grafu `BUILDING` a ze zamčeného situačního plánu.
 */

import { t } from '@reos/i18n';
import { caseHeader, type CaseDetail } from '@reos/reports';

export type CaseSubjectType =
  | 'LAND'
  | 'LAND_MANY'
  | 'HOUSE'
  | 'HOUSE_WITH_LAND'
  | 'FLAT'
  | 'OTHER';

export interface CaseSubject {
  /** Co z dat vyplývá jako předmět zakázky. */
  readonly type: CaseSubjectType;
  /** Český popisek pro makléře. */
  readonly label: string;
  /** Co uvedl makléř při založení (`dd_cases.kind`) — jen pro přiznání rozporu. */
  readonly declaredLabel: string;
  /** Zadání makléře s realitou nesouhlasí; UI to musí říct, ne schovat. */
  readonly declaredDiffers: boolean;
  /** Parcel v rozsahu (null = nevíme), jen pro popisek a testy. */
  readonly parcelCount: number | null;
  /**
   * Zadani maklere v podobe, jakou zna `decisionReadiness`
   * (`MANDATORY_GATES_BY_CASE_KIND`). `null` = jiny typ, nez umi server
   * rozpoznat — pak je jmenovatel bran nevim jaky a nesmime se hadat.
   */
  readonly declaredGateKind: GateCaseKind | null;
}

const CAPTIONS: Readonly<Record<CaseSubjectType, string>> = {
  LAND: t('webAdmin.caseSubject.land', { locale: 'cs' }),
  LAND_MANY: t('webAdmin.caseSubject.landMany', { locale: 'cs' }),
  HOUSE: t('webAdmin.caseSubject.house', { locale: 'cs' }),
  HOUSE_WITH_LAND: t('webAdmin.caseSubject.houseWithLand', { locale: 'cs' }),
  FLAT: t('webAdmin.caseSubject.flat', { locale: 'cs' }),
  OTHER: t('webAdmin.caseSubject.other', { locale: 'cs' }),
};

const DECLARED_CAPTIONS: Readonly<Record<string, string>> = {
  LAND: CAPTIONS.LAND,
  HOUSE: CAPTIONS.HOUSE,
  FLAT: CAPTIONS.FLAT,
};

export interface CaseSubjectInput {
  /** `dd_cases.kind` — zadání makléře (LAND | HOUSE | FLAT). */
  readonly declaredKind: string;
  /** Počet parcel v rozsahu; `null` = rozsah zatím neznámý. */
  readonly parcelCount: number | null;
  /** Je v datech doložená stavba (fakt, uzel grafu, situační plán)? */
  readonly hasBuildingEvidence: boolean;
}

/**
 * Čistý výpočet nad třemi hodnotami — bez DOM, bez fetch, bez detailu.
 * Pravidlo: předmětem je to, co je v datech; zadání se při tom neruší,
 * jen se vedle něj přizná.
 */
export function resolveCaseSubject(input: CaseSubjectInput): CaseSubject {
  const declaredLabel = DECLARED_CAPTIONS[input.declaredKind] ?? CAPTIONS.OTHER;
  const parcels = input.parcelCount;
  const hasParcels = parcels !== null && parcels > 0;
  const manyParcels = hasParcels && (parcels ?? 0) > 1;

  let type: CaseSubjectType;
  if (input.declaredKind === 'FLAT') {
    // Byt je byt; parcely v domě o něm nerozhodují.
    type = 'FLAT';
  } else if (input.hasBuildingEvidence) {
    type = manyParcels ? 'HOUSE_WITH_LAND' : 'HOUSE';
  } else if (manyParcels) {
    type = 'LAND_MANY';
  } else if (hasParcels) {
    type = 'LAND';
  } else if (input.declaredKind === 'HOUSE') {
    // Nic nevylučuje zadání — nelze tvrdit, že se makléř mýlil.
    type = 'HOUSE';
  } else if (input.declaredKind === 'LAND') {
    type = 'LAND';
  } else {
    type = 'OTHER';
  }

  return {
    type,
    label: CAPTIONS[type],
    declaredLabel,
    declaredDiffers: familyOf(input.declaredKind) !== null && familyOf(input.declaredKind) !== familyOf(type),
    parcelCount: parcels,
    declaredGateKind: GATE_KINDS[input.declaredKind] ?? null,
  };
}

/** Klic `MANDATORY_GATES_BY_CASE_KIND` — tri typy, ktere server umi. */
export type GateCaseKind = 'LAND' | 'HOUSE' | 'FLAT';

const GATE_KINDS: Readonly<Record<string, GateCaseKind>> = {
  LAND: 'LAND',
  HOUSE: 'HOUSE',
  FLAT: 'FLAT',
};

export type SubjectFamily = 'FLAT' | 'HOUSE' | 'LAND';

/** Rod předmětu, s kterým lze zadání porovnat (HOUSE a HOUSE_WITH_LAND = dům). */
function familyOf(subject: string): SubjectFamily | null {
  if (subject === 'FLAT') return 'FLAT';
  if (subject === 'HOUSE' || subject === 'HOUSE_WITH_LAND') return 'HOUSE';
  if (subject === 'LAND' || subject === 'LAND_MANY') return 'LAND';
  return null;
}

/** Je v datech zakázky doložená stavba? Fakt, uzel grafu, nebo situační plán. */
function hasBuildingEvidence(detail: CaseDetail): boolean {
  if (detail.facts.some((fact) => fact.factKey.startsWith('building.'))) return true;
  if (detail.fixture.nodes.some((node) => node.nodeType === 'BUILDING')) return true;
  const geometry = detail.fixture.sitePlan?.geometry;
  if (geometry === undefined || geometry === null) return false;
  const buildings = (geometry as { readonly buildings?: unknown }).buildings;
  if (Array.isArray(buildings) && buildings.length > 0) return true;
  return (geometry as { readonly building?: unknown }).building !== null;
}

/** Předmět zakázky pro UI — volá se na všech místech, kde dnes visí štítek. */
export function caseSubjectOf(detail: CaseDetail): CaseSubject {
  const header = caseHeader(detail);
  return resolveCaseSubject({
    declaredKind: header.kind,
    parcelCount: header.parcelCount,
    hasBuildingEvidence: hasBuildingEvidence(detail),
  });
}

/**
 * Krok UX2 — typ, podle kterého se počítají POVINNÉ KONTROLY.
 *
 * Nález R.B. 29. 9. 2026 (zakázka Poděbradova 141/44): hlavička psala
 * „Dům s pozemky", `dd_cases.kind` přitom neslo `LAND` (makléř v průvodci
 * vybral „Pozemek") a `decisionReadiness` podle toho počítal brány pro POZEMEK
 * — 4 místo 5, bez kontroly legálnosti stavby a statické bezpečnosti.
 *
 * ROZHODNUTÍ UX2: jmenovatel i stav kokpitu berou **zjištěný** předmět, ne
 * zadání. Výpočet v `packages/contexts/duediligence` se v tomto kroku NEMĚNÍ
 * (zlatý standard) — server-side nález je v REPORT-step-UX2.md pro linku A.
 * Tady je to proste: zjištěný předmět zkusime srazit na klic, ktery server zna.
 */
const DETECTED_GATE_KINDS: Readonly<Record<CaseSubjectType, GateCaseKind>> = {
  LAND: 'LAND',
  LAND_MANY: 'LAND',
  HOUSE: 'HOUSE',
  HOUSE_WITH_LAND: 'HOUSE',
  FLAT: 'FLAT',
  OTHER: 'LAND',
};

/**
 * Zjištěný typ jako klic pro `MANDATORY_GATES_BY_CASE_KIND`. Kdyz předmět
 * nejde přebrat ani na zadání, vrati `null` — jmenovatel se pak nehádat.
 */
export function gateCaseKindOf(subject: CaseSubject): GateCaseKind | null {
  if (subject.type === 'OTHER') return subject.declaredGateKind;
  return DETECTED_GATE_KINDS[subject.type];
}
